import React from 'react';
import { Send, HelpCircle } from 'lucide-react';
import { Question } from '../../types';

interface TriageInputFormProps {
  question: Question;
  inputs: string;
  target: string;
  theory: string;
  formula: string;
  onInputChange: (field: 'inputs' | 'target' | 'theory' | 'formula', value: string) => void;
  onSubmit: () => void;
  onStuck: () => void;
}

export const TriageInputForm: React.FC<TriageInputFormProps> = ({
  question,
  inputs,
  target,
  theory,
  formula,
  onInputChange,
  onSubmit,
  onStuck,
}) => {
  const isExactScience =
    question.subject === 'MATEMATICA' ||
    question.subject === 'FISICA' ||
    (question.subject === 'QUIMICA' &&
      question.formulaBridge &&
      question.formulaBridge !== 'Relação conceitual direta' &&
      question.formulaBridge !== 'Postulados de reatividade');

  return (
    <div className="mt-6 border-t border-paper-200 pt-5 dark:border-study-line space-y-4">
      {isExactScience ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
                1. Dados Fornecidos (Inputs com unidades)
              </label>
              <input
                type="text"
                value={inputs}
                onChange={(e) => onInputChange('inputs', e.target.value)}
                placeholder=""
                className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs font-mono text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
                2. Incógnita Alvo (Output)
              </label>
              <input
                type="text"
                value={target}
                onChange={(e) => onInputChange('target', e.target.value)}
                placeholder=""
                className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs font-mono text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
                3. Gatilho / Teoria (Model Tag)
              </label>
              <input
                type="text"
                value={theory}
                onChange={(e) => onInputChange('theory', e.target.value)}
                placeholder=""
                className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
                4. Fórmula-Ponte Bruta
              </label>
              <input
                type="text"
                value={formula}
                onChange={(e) => onInputChange('formula', e.target.value)}
                placeholder=""
                className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs font-mono text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
              />
            </div>
          </div>
        </>
      ) : question.subject === 'LINGUAGENS' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
              1. Gatilho Conceitual / Tese Central
            </label>
            <input
              type="text"
              value={theory}
              onChange={(e) => onInputChange('theory', e.target.value)}
              placeholder=""
              className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
              2. Comando da Questão / Objeto de Análise
            </label>
            <input
              type="text"
              value={target}
              onChange={(e) => onInputChange('target', e.target.value)}
              placeholder=""
              className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
            />
          </div>
        </div>
      ) : question.subject === 'HUMANAS' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
              1. Contexto Histórico / Conceito Geográfico
            </label>
            <input
              type="text"
              value={theory}
              onChange={(e) => onInputChange('theory', e.target.value)}
              placeholder=""
              className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
              2. Relação Causal / Objeto de Análise
            </label>
            <input
              type="text"
              value={target}
              onChange={(e) => onInputChange('target', e.target.value)}
              placeholder=""
              className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
              1. Conceito-Chave / Fenômeno
            </label>
            <input
              type="text"
              value={theory}
              onChange={(e) => onInputChange('theory', e.target.value)}
              placeholder=""
              className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400 mb-1">
              2. Processo / Relação Funcional
            </label>
            <input
              type="text"
              value={target}
              onChange={(e) => onInputChange('target', e.target.value)}
              placeholder=""
              className="w-full rounded border border-paper-300 bg-paper-50 px-3 py-2 text-xs text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
            />
          </div>
        </div>
      )}

      {/* Submit and stuck action buttons */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onSubmit}
          className="flex flex-1 items-center justify-center gap-2 rounded bg-zinc-900 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-zinc-800 active:scale-[0.99] dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white transition"
        >
          <Send className="h-3.5 w-3.5" />
          Conferir Decodificação
        </button>

        <button
          type="button"
          onClick={onStuck}
          className="flex items-center gap-1.5 rounded border border-paper-300 bg-paper-50 px-4 py-2.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 dark:border-study-line dark:bg-study-card dark:text-amber-400 dark:hover:bg-amber-950/30 transition"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          Travei / Não Sei
        </button>
      </div>
    </div>
  );
};
